import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { clientService } from '@/lib/supabase/clients';
import { tripService } from '@/lib/supabase/trips';
import { quotationService } from '@/lib/supabase/quotations';
import { useToast } from '@/hooks/use-toast';

enum MigrationStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

interface MigrationState {
  clients: MigrationStatus;
  trips: MigrationStatus;
  quotations: MigrationStatus;
}

export default function DataMigration() {
  const { toast } = useToast();
  const [migrationState, setMigrationState] = useState<MigrationState>({
    clients: MigrationStatus.PENDING,
    trips: MigrationStatus.PENDING,
    quotations: MigrationStatus.PENDING,
  });
  
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [tripCount, setTripCount] = useState<number | null>(null);
  const [quotationCount, setQuotationCount] = useState<number | null>(null);
  
  // Carregar contagens do localStorage
  useEffect(() => {
    try {
      // Clientes
      const clientsData = localStorage.getItem('voah_clients');
      if (clientsData) {
        const clients = JSON.parse(clientsData);
        setClientCount(Array.isArray(clients) ? clients.length : 0);
      }
      
      // Viagens
      const tripsData = localStorage.getItem('voah_trips');
      if (tripsData) {
        const trips = JSON.parse(tripsData);
        setTripCount(Array.isArray(trips) ? trips.length : 0);
      }
      
      // Cotações (draft + saved)
      let quotationTotal = 0;
      const quotationsData = localStorage.getItem('voah_quotations');
      if (quotationsData) {
        const quotations = JSON.parse(quotationsData);
        if (Array.isArray(quotations)) {
          quotationTotal += quotations.length;
        }
      }
      
      const draftData = localStorage.getItem('voah_quotation_draft');
      if (draftData) {
        try {
          const draft = JSON.parse(draftData);
          if (draft && draft.id) {
            quotationTotal += 1;
          }
        } catch (e) {
          console.error('Erro ao processar rascunho de cotação:', e);
        }
      }
      
      setQuotationCount(quotationTotal);
    } catch (e) {
      console.error('Erro ao ler dados do localStorage:', e);
      toast({
        title: 'Erro',
        description: 'Não foi possível ler dados do localStorage',
        variant: 'destructive',
      });
    }
  }, [toast]);
  
  // Migrar clientes
  const migrateClients = async () => {
    setMigrationState(prev => ({ ...prev, clients: MigrationStatus.RUNNING }));
    
    try {
      const result = await clientService.migrateLocalClients();
      
      if (result) {
        setMigrationState(prev => ({ ...prev, clients: MigrationStatus.SUCCESS }));
        toast({
          title: 'Sucesso',
          description: 'Clientes migrados com sucesso',
        });
      } else {
        setMigrationState(prev => ({ ...prev, clients: MigrationStatus.FAILURE }));
        toast({
          title: 'Atenção',
          description: 'Não havia clientes para migrar',
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Erro ao migrar clientes:', error);
      setMigrationState(prev => ({ ...prev, clients: MigrationStatus.FAILURE }));
      toast({
        title: 'Erro',
        description: 'Falha ao migrar clientes para o Supabase',
        variant: 'destructive',
      });
    }
  };
  
  // Migrar viagens
  const migrateTrips = async () => {
    setMigrationState(prev => ({ ...prev, trips: MigrationStatus.RUNNING }));
    
    try {
      const result = await tripService.migrateLocalTrips();
      
      if (result) {
        setMigrationState(prev => ({ ...prev, trips: MigrationStatus.SUCCESS }));
        toast({
          title: 'Sucesso',
          description: 'Viagens migradas com sucesso',
        });
      } else {
        setMigrationState(prev => ({ ...prev, trips: MigrationStatus.FAILURE }));
        toast({
          title: 'Atenção',
          description: 'Não havia viagens para migrar',
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Erro ao migrar viagens:', error);
      setMigrationState(prev => ({ ...prev, trips: MigrationStatus.FAILURE }));
      toast({
        title: 'Erro',
        description: 'Falha ao migrar viagens para o Supabase',
        variant: 'destructive',
      });
    }
  };
  
  // Migrar cotações
  const migrateQuotations = async () => {
    setMigrationState(prev => ({ ...prev, quotations: MigrationStatus.RUNNING }));
    
    try {
      const result = await quotationService.migrateLocalQuotations();
      
      if (result) {
        setMigrationState(prev => ({ ...prev, quotations: MigrationStatus.SUCCESS }));
        toast({
          title: 'Sucesso',
          description: 'Cotações migradas com sucesso',
        });
      } else {
        setMigrationState(prev => ({ ...prev, quotations: MigrationStatus.FAILURE }));
        toast({
          title: 'Atenção',
          description: 'Não havia cotações para migrar',
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Erro ao migrar cotações:', error);
      setMigrationState(prev => ({ ...prev, quotations: MigrationStatus.FAILURE }));
      toast({
        title: 'Erro',
        description: 'Falha ao migrar cotações para o Supabase',
        variant: 'destructive',
      });
    }
  };
  
  // Migrar todos os dados
  const migrateAll = async () => {
    // Reset status
    setMigrationState({
      clients: MigrationStatus.PENDING,
      trips: MigrationStatus.PENDING,
      quotations: MigrationStatus.PENDING,
    });
    
    // Migrar clientes primeiro
    setMigrationState(prev => ({ ...prev, clients: MigrationStatus.RUNNING }));
    try {
      await clientService.migrateLocalClients();
      setMigrationState(prev => ({ ...prev, clients: MigrationStatus.SUCCESS }));
      
      // Depois migrar viagens
      setMigrationState(prev => ({ ...prev, trips: MigrationStatus.RUNNING }));
      await tripService.migrateLocalTrips();
      setMigrationState(prev => ({ ...prev, trips: MigrationStatus.SUCCESS }));
      
      // Por fim, migrar cotações
      setMigrationState(prev => ({ ...prev, quotations: MigrationStatus.RUNNING }));
      await quotationService.migrateLocalQuotations();
      setMigrationState(prev => ({ ...prev, quotations: MigrationStatus.SUCCESS }));
      
      toast({
        title: 'Sucesso',
        description: 'Todos os dados foram migrados com sucesso!',
      });
    } catch (error) {
      console.error('Erro durante migração completa:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro durante a migração de dados',
        variant: 'destructive',
      });
    }
  };
  
  // Renderizar o status da migração
  const renderStatus = (status: MigrationStatus) => {
    switch (status) {
      case MigrationStatus.PENDING:
        return <Badge variant="outline">Pendente</Badge>;
      case MigrationStatus.RUNNING:
        return <Badge variant="secondary" className="flex items-center"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Migrando...</Badge>;
      case MigrationStatus.SUCCESS:
        return <Badge variant="default" className="bg-green-500 flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Concluído</Badge>;
      case MigrationStatus.FAILURE:
        return <Badge variant="destructive" className="flex items-center"><XCircle className="w-3 h-3 mr-1" /> Falha</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">Migração de Dados para Supabase</h1>
      
      <Alert className="mb-6">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Atenção</AlertTitle>
        <AlertDescription>
          Esta página permite migrar seus dados do localStorage para o banco de dados Supabase.
          A migração preservará todos os IDs originais e relacionamentos entre os dados.
          É recomendável executar a migração apenas uma vez para evitar duplicações.
        </AlertDescription>
      </Alert>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados Locais</CardTitle>
            <CardDescription>Dados encontrados no localStorage do navegador</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Clientes:</span>
                <Badge variant="outline">{clientCount ?? '...'}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span>Viagens:</span>
                <Badge variant="outline">{tripCount ?? '...'}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span>Cotações:</span>
                <Badge variant="outline">{quotationCount ?? '...'}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Status da Migração</CardTitle>
            <CardDescription>Acompanhe o progresso da migração</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Clientes:</span>
                {renderStatus(migrationState.clients)}
              </div>
              <div className="flex justify-between items-center">
                <span>Viagens:</span>
                {renderStatus(migrationState.trips)}
              </div>
              <div className="flex justify-between items-center">
                <span>Cotações:</span>
                {renderStatus(migrationState.quotations)}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={migrateAll}
              disabled={
                migrationState.clients === MigrationStatus.RUNNING ||
                migrationState.trips === MigrationStatus.RUNNING ||
                migrationState.quotations === MigrationStatus.RUNNING
              }
            >
              Migrar Tudo
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      <Tabs defaultValue="clients" className="mt-6">
        <TabsList>
          <TabsTrigger value="clients">Clientes</TabsTrigger>
          <TabsTrigger value="trips">Viagens</TabsTrigger>
          <TabsTrigger value="quotations">Cotações</TabsTrigger>
        </TabsList>
        
        <TabsContent value="clients">
          <Card>
            <CardHeader>
              <CardTitle>Migração de Clientes</CardTitle>
              <CardDescription>
                Migre {clientCount ?? 0} clientes do localStorage para o Supabase
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>
                Esta operação irá transferir todos os clientes armazenados localmente
                para o banco de dados Supabase, preservando todos os IDs e relacionamentos.
              </p>
            </CardContent>
            <CardFooter>
              <Button
                onClick={migrateClients}
                disabled={migrationState.clients === MigrationStatus.RUNNING}
              >
                {migrationState.clients === MigrationStatus.RUNNING && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Migrar Clientes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="trips">
          <Card>
            <CardHeader>
              <CardTitle>Migração de Viagens</CardTitle>
              <CardDescription>
                Migre {tripCount ?? 0} viagens do localStorage para o Supabase
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>
                Esta operação irá transferir todas as viagens armazenadas localmente
                para o banco de dados Supabase, preservando todos os IDs e relacionamentos.
              </p>
            </CardContent>
            <CardFooter>
              <Button
                onClick={migrateTrips}
                disabled={migrationState.trips === MigrationStatus.RUNNING}
              >
                {migrationState.trips === MigrationStatus.RUNNING && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Migrar Viagens
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="quotations">
          <Card>
            <CardHeader>
              <CardTitle>Migração de Cotações</CardTitle>
              <CardDescription>
                Migre {quotationCount ?? 0} cotações do localStorage para o Supabase
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>
                Esta operação irá transferir todas as cotações armazenadas localmente
                para o banco de dados Supabase, preservando todos os IDs e relacionamentos.
              </p>
            </CardContent>
            <CardFooter>
              <Button
                onClick={migrateQuotations}
                disabled={migrationState.quotations === MigrationStatus.RUNNING}
              >
                {migrationState.quotations === MigrationStatus.RUNNING && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Migrar Cotações
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 